/**
 * Chassis-page manual-ops / profiles / verify panels — GPON-only.
 *
 * v0.34.0 (WS-G) dissolved the old Deployment tab into the new IA:
 * ``ManualOpsPanel`` (the six destructive maintenance forms, flattened
 * one level) mounts under Provisioning → Manual ops, ``ProfilesPanel``
 * under Provisioning → Config profiles, and ``VerifyPanel`` under
 * Diagnostics → Backups (all in ChassisDetail).
 *
 * Every destructive action is gated behind the two-click ConfirmDialog
 * (open + deliberate second click — no typed sentences); submitting
 * hands the resulting job off to the InlineJobTracker via the
 * standard ``inline-job-tracker.add`` custom event so the operator
 * stays on the chassis page through the whole flow.
 *
 * The active form + prefill ride the URL hash
 * (``#tab=provisioning&sub=manual&op=deprovision&port=1&display_id=104&serial=…``)
 * so the PonPortsCard per-row menu can deep-link straight into a form
 * with port / display-id / serial already filled in.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ChevronRight, FileSearch, Play, RefreshCw } from "lucide-react";
import { useCallback, useEffect, useMemo, useState } from "react";

import { api, apiErrorMessage } from "../../../../api/client";
import { LocalTime, formatLocal } from "../../../../lib/datetime";
import { SelectOrCustom } from "../../../../components/SelectOrCustom";
import { Badge, type BadgeTone } from "../../../components/Badge";
import {
  IMPACT_LABEL,
  IMPACT_TONE,
  agoLabel,
  impactOf,
} from "../../../lib/clearImpact";
import {
  type BulkJobDetailView,
  type BulkJobDetailWire,
  CHILD_JOB_STATUS_TONE,
  ChildJobsTable,
  normaliseBulkJobDetail,
} from "../../../components/ChildJobsTable";
import { CommandPreview } from "../../../components/CommandPreview";
import { useConfirm } from "../../../components/ConfirmDialog";
import { CopyableCell } from "../../../components/CopyableCell";
import { Tabs, type TabSpec } from "../../../components/Tabs";
import { useToast } from "../../../components/Toast";
import {
  notOnOlt,
  type KnownProfiles,
} from "../../../components/ServiceSpecsEditor";
import { HelpButton } from "../../../help/HelpPanel";
import { InlineJobTracker } from "../InlineJobTracker";

function readHashOp(fallback: string): string {
  if (typeof window === "undefined") return fallback;
  const hash = window.location.hash || "";
  const match = hash.match(/[#&]op=([\w-]+)/);
  return match ? match[1] : fallback;
}

function writeHashOp(value: string) {
  if (typeof window === "undefined") return;
  const url = new URL(window.location.href);
  const hash = url.hash || "#";
  const cleaned = hash.replace(/[&#]op=[\w-]+/, "");
  url.hash = (cleaned || "#") + (cleaned.includes("=") ? "&" : "") + `op=${value}`;
  history.replaceState(null, "", url.toString());
}

/** Row-menu deep-link payload — seeds the matching form's fields. */
export type ManualOpPrefill = {
  port?: string;
  displayId?: string;
  serial?: string;
  /** Add-service scope (``onu`` / ``port`` / ``chassis``); other forms ignore it. */
  scope?: string;
};

function readHashPrefill(): ManualOpPrefill {
  if (typeof window === "undefined") return {};
  const params = new URLSearchParams((window.location.hash || "#").slice(1));
  const pick = (key: string): string | undefined => {
    const v = params.get(key)?.trim();
    return v ? v : undefined;
  };
  return {
    port: pick("port"),
    displayId: pick("display_id"),
    serial: pick("serial"),
    scope: pick("scope"),
  };
}

export function ManualOpsPanel({ chassisId }: { chassisId: string }) {
  const [op, setOp] = useState<string>(() => readHashOp("move"));
  const [prefill, setPrefill] = useState<ManualOpPrefill>(() =>
    readHashPrefill(),
  );

  const onOpChange = useCallback((next: string) => {
    setOp(next);
    writeHashOp(next);
  }, []);

  useEffect(() => {
    const onHash = () => {
      setOp(readHashOp("move"));
      setPrefill(readHashPrefill());
    };
    window.addEventListener("hashchange", onHash);
    return () => window.removeEventListener("hashchange", onHash);
  }, []);

  // Remount the forms whenever a row-menu deep-link lands new prefill
  // values — the fields are plain useState initialisers, so a fresh
  // key is what re-seeds them.
  const prefillKey = `${prefill.port ?? ""}|${prefill.displayId ?? ""}|${prefill.serial ?? ""}|${prefill.scope ?? ""}`;

  const ops = useMemo<TabSpec[]>(
    () => [
      {
        value: "move",
        label: "Move ONU",
        content: (
          <MoveOnuForm key={prefillKey} chassisId={chassisId} prefill={prefill} />
        ),
      },
      {
        value: "bulk-clear",
        label: "Bulk PON clear",
        content: (
          <BulkPonClearForm
            key={prefillKey}
            chassisId={chassisId}
            prefill={prefill}
          />
        ),
      },
      {
        value: "replace-vnet",
        label: "Replace VNET",
        content: (
          <ReplaceVnetForm
            key={prefillKey}
            chassisId={chassisId}
            prefill={prefill}
          />
        ),
      },
      {
        value: "add-service",
        label: "Add service",
        content: (
          <AddServiceForm
            key={prefillKey}
            chassisId={chassisId}
            prefill={prefill}
          />
        ),
      },
      {
        value: "provision",
        label: "Provision ONU",
        content: (
          <ProvisionOnuForm
            key={prefillKey}
            chassisId={chassisId}
            prefill={prefill}
          />
        ),
      },
      {
        value: "deprovision",
        label: "Deprovision ONU",
        content: (
          <DeprovisionOnuForm
            key={prefillKey}
            chassisId={chassisId}
            prefill={prefill}
          />
        ),
      },
      {
        value: "swap",
        label: "Swap ONU",
        content: (
          <SwapOnuForm key={prefillKey} chassisId={chassisId} prefill={prefill} />
        ),
      },
    ],
    [chassisId, prefill, prefillKey],
  );
  return (
    <div className="flex flex-col gap-3">
      <section className="rounded-lg bg-slate-900 shadow-border p-4">
        <div className="mb-3">
          <h3 className="text-sm font-medium text-slate-200">
            Maintenance &amp; destructive ops
          </h3>
          <p className="text-[10px] text-slate-500">
            All actions are confirm-gated (two-click); a whole-chassis
            Add service also needs a typed confirm.
            Submitting hands the job off to the inline tracker; locks +
            audit fire as normal.
          </p>
        </div>
        <Tabs tabs={ops} value={op} onValueChange={onOpChange} />
      </section>

      {/* Sticky inline tracker — only renders when at least one job
          is tracked. The tracker itself handles its own visibility. */}
      <InlineJobTracker />
    </div>
  );
}

// ----------------------------------------------------------------------
// Shared helpers — two-click confirm + job submission
// ----------------------------------------------------------------------

const inputCls =
  "w-full rounded border border-slate-700 bg-slate-950 px-2 py-1 text-sm text-slate-100 placeholder:text-slate-600 focus-visible:border-emerald-700 focus-visible:outline-none";

function Field({
  label,
  hint,
  children,
}: {
  label: string;
  hint?: string;
  children: React.ReactNode;
}) {
  return (
    <label className="flex flex-col gap-1 text-xs">
      <span className="text-slate-400">{label}</span>
      {children}
      {hint && <span className="text-[10px] text-slate-500">{hint}</span>}
    </label>
  );
}

/** Two-click confirm and submit a job. Returns true when the job was
 *  queued, false when the operator cancelled the confirm. Callers must
 *  render the returned ``confirmDialog`` node so the dialog can mount;
 *  ``previewText`` (when available) embeds the rendered CLI block in
 *  the dialog so the operator confirms against the exact commands. */
function useSubmitDestructive(chassisId: string) {
  const toast = useToast();
  const { confirm, confirmDialog } = useConfirm();
  const submit = async (
    jobName: string,
    params: Record<string, unknown>,
    summary: string,
    previewText?: string | null,
  ): Promise<boolean> => {
    const ok = await confirm({
      title: `Confirm ${jobName}`,
      summary,
      children: previewText ? <CommandPreview text={previewText} /> : undefined,
      confirmLabel: "Submit (destructive)",
    });
    if (!ok) return false;
    try {
      const res = await api.post<{ id: string }>(
        `/chassis/${chassisId}/jobs`,
        { name: jobName, params },
      );
      window.dispatchEvent(
        new CustomEvent("inline-job-tracker.add", {
          detail: { jobId: res.data.id, kind: jobName },
        }),
      );
      toast.push(`${jobName} queued`, "success");
      return true;
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error("destructive submit failed", err);
      toast.push(apiErrorMessage(err, "Submit failed — check the form"), "error");
      return false;
    }
  };
  return { submit, confirmDialog };
}

// Shared inventory lookup for the maintenance forms. Feeds the Port /
// Display-ID dropdowns so operators pick the EXACT values the backend
// stored in onu_inventory (no "PON1" vs "1" vs "SFPPON-1" mismatch —
// the bulk-clear preview + deprovision arm match ``port`` verbatim).
type InventoryOnu = {
  display_id: string;
  serial: string | null;
  state: string | null;
};
type InventoryPort = { port: string; onu_count: number; onus: InventoryOnu[] };
type InventoryResponse = { ports: InventoryPort[] };

function useInventory(chassisId: string) {
  const q = useQuery<InventoryResponse>({
    // Same key as PonPortsCard so the two share one cache entry.
    queryKey: ["v2", "chassis", chassisId, "inventory"],
    queryFn: async () => {
      const res = await api.get<InventoryResponse>(
        `/chassis/${chassisId}/inventory`,
      );
      return res.data;
    },
  });
  const ports = (q.data?.ports ?? []).map((p) => p.port);
  const onusForPort = (port: string): InventoryOnu[] =>
    q.data?.ports.find((p) => p.port === port)?.onus ?? [];
  const displayIdsForPort = (port: string): string[] =>
    onusForPort(port).map((o) => o.display_id);
  return { ports, displayIdsForPort, onusForPort };
}

// ----------------------------------------------------------------------
// Move ONU
// ----------------------------------------------------------------------

type MovePreview = { lines: string[] };
/** Shared shape for the provision / deprovision / swap preview endpoints. */
type CliPreview = { lines: string[]; rendered_block: string };

function MoveOnuForm({
  chassisId,
  prefill,
}: {
  chassisId: string;
  prefill?: ManualOpPrefill;
}) {
  const [fromPort, setFromPort] = useState(prefill?.port ?? "");
  const [fromDisplayId, setFromDisplayId] = useState(prefill?.displayId ?? "");
  const [toPort, setToPort] = useState("");
  const [toDisplayId, setToDisplayId] = useState("");
  const [toOnuId, setToOnuId] = useState("");
  const [serial, setSerial] = useState(prefill?.serial ?? "");
  const [preview, setPreview] = useState<string[] | null>(null);
  const { submit, confirmDialog } = useSubmitDestructive(chassisId);
  const toast = useToast();

  const previewMut = useMutation({
    mutationFn: async () => {
      const res = await api.post<MovePreview>(
        `/chassis/${chassisId}/maintenance/move-onu/preview`,
        {
          from_port: fromPort,
          from_display_id: fromDisplayId,
          to_port: toPort,
          to_display_id: toDisplayId,
          to_onu_id: toOnuId.trim() ? parseInt(toOnuId, 10) : null,
          serial: serial.trim() || null,
          services: [],
        },
      );
      return res.data;
    },
    onSuccess: (data) => setPreview(data.lines),
    onError: () => toast.push("Preview failed — check the inputs", "error"),
  });

  const run = async () => {
    const summary = `move ${fromPort}/${fromDisplayId} -> ${toPort}/${toDisplayId}`;
    const queued = await submit(
      "move_onu",
      {
        from_port: fromPort,
        from_display_id: fromDisplayId,
        to_port: toPort,
        to_display_id: toDisplayId,
        to_onu_id: toOnuId.trim() ? parseInt(toOnuId, 10) : null,
        serial: serial.trim() || null,
      },
      summary,
      preview ? preview.join("\n") : null,
    );
    if (queued) setPreview(null);
  };

  return (
    <div className="grid gap-3 sm:grid-cols-2">
      <Field label="From port" hint="e.g. 1">
        <input
          type="text"
          value={fromPort}
          onChange={(e) => setFromPort(e.target.value)}
          className={inputCls}
        />
      </Field>
      <Field label="From display ID" hint="current display ID">
        <input
          type="text"
          value={fromDisplayId}
          onChange={(e) => setFromDisplayId(e.target.value)}
          className={inputCls}
        />
      </Field>
      <Field label="To port" hint="target PON port">
        <input
          type="text"
          value={toPort}
          onChange={(e) => setToPort(e.target.value)}
          className={inputCls}
        />
      </Field>
      <Field label="To display ID" hint="new display ID">
        <input
          type="text"
          value={toDisplayId}
          onChange={(e) => setToDisplayId(e.target.value)}
          className={inputCls}
        />
      </Field>
      <Field label="To ONU id (optional)">
        <input
          type="number"
          value={toOnuId}
          onChange={(e) => setToOnuId(e.target.value)}
          className={inputCls}
        />
      </Field>
      <Field label="Serial (optional)">
        <input
          type="text"
          value={serial}
          onChange={(e) => setSerial(e.target.value)}
          className={inputCls}
          spellCheck={false}
        />
      </Field>
      <div className="sm:col-span-2 flex items-center gap-2 pt-2">
        <button
          type="button"
          onClick={() => previewMut.mutate()}
          disabled={previewMut.isPending || !fromPort || !fromDisplayId || !toPort || !toDisplayId}
          className="inline-flex items-center gap-1 rounded border border-slate-700 bg-slate-800 px-3 py-1.5 text-xs text-slate-200 hover:bg-slate-700 disabled:opacity-50"
        >
          <FileSearch className="h-3 w-3" />
          {previewMut.isPending ? "Rendering…" : "Preview CLI"}
        </button>
        <button
          type="button"
          onClick={run}
          disabled={!fromPort || !fromDisplayId || !toPort || !toDisplayId}
          className="inline-flex items-center gap-1 rounded border border-red-800 bg-red-900/30 px-3 py-1.5 text-xs text-red-200 hover:bg-red-900/50 disabled:opacity-50"
        >
          <Play className="h-3 w-3" />
          Submit move (destructive)
        </button>
      </div>
      {preview && (
        <div className="sm:col-span-2">
          <CommandPreview text={preview.join("\n")} />
        </div>
      )}
      {confirmDialog}
    </div>
  );
}

// ----------------------------------------------------------------------
// Bulk PON clear
// ----------------------------------------------------------------------

type BulkClearOnu = {
  port: string;
  display_id: string;
  serial: string | null;
  state: string | null;
  /** Service context — empty/zero when no MAC was ever collected.
   *  Optional on the wire: a server older than v0.35 omits these
   *  entirely, and a cached preview in React state can predate a
   *  backend deploy. Treat absence as "unknown", never as "none". */
  vnets?: string[];
  gigafy_devices?: string[];
  live_sessions?: number;
  mac_seen_at?: string | null;
  occupied?: boolean | null;
};
type BulkClearServiceSummary = {
  onus_total: number;
  with_mac_data: number;
  with_gigafy_device: number;
  with_live_session: number;
  /** v0.37.5+; absent on older servers. */
  with_occupied?: number;
  unknown: number;
  macs_collected_at: string | null;
  pppoe_observed_at: string | null;
  devices_resolved_at?: string | null;
};
type BulkClearPreview = {
  scope: string;
  port: string | null;
  onus: BulkClearOnu[];
  /** Absent when talking to a pre-v0.35 server; the panel is skipped. */
  service_summary?: BulkClearServiceSummary;
  rendered_block: string;
  confirm_expected: string;
};

/**
 * The honest headline for a bulk clear.
 *
 * Leads with the worst confirmed impact, then — always, and separately
 * — states how much of the port we simply have no evidence for. The
 * second sentence is the one that matters on a chassis whose MAC
 * sweeps are stale: 28 unknown ONUs must never render as 28 safe ones.
 */
function BulkClearImpact({ s }: { s: BulkClearServiceSummary }) {
  const occupied = s.with_occupied ?? 0;
  const provisioned = Math.max(s.with_gigafy_device, occupied);
  const headlineTone: BadgeTone =
    s.with_live_session > 0
      ? "danger"
      : provisioned > 0
        ? "warning"
        : "muted";
  const occupiedNote =
    occupied > 0 ? ` (${occupied} marked moved-in on the portal)` : "";
  const headline =
    s.with_live_session > 0
      ? `${s.with_live_session} of ${s.onus_total} have a live PPPoE session — this clear drops live customers.`
      : provisioned > 0
        ? `${provisioned} of ${s.onus_total} are provisioned to a known Gigafy device${occupiedNote}.`
        : "No ONU on this port resolved to a Gigafy device.";
  // Portal-sourced device sync writes names but no MikroTik sessions,
  // so "0 live" there is absence of evidence, not an idle line.
  const noLiveProof = !s.pppoe_observed_at && !!s.devices_resolved_at;

  return (
    <div className="flex flex-col gap-1.5 rounded-lg bg-slate-900 p-3 shadow-border">
      <div className="flex flex-wrap items-center gap-2">
        <Badge tone={headlineTone}>customer impact</Badge>
        <span className="text-xs text-slate-200">{headline}</span>
      </div>

      {s.unknown > 0 && (
        <p className="text-[11px] text-amber-300">
          <strong className="font-semibold">{s.unknown}</strong> of{" "}
          {s.onus_total} have no collected MAC — their service state is{" "}
          <strong className="font-semibold">unknown</strong>, not confirmed
          empty. Run <span className="font-mono">Collect MAC table</span>{" "}
          before treating this port as vacant.
        </p>
      )}

      {/* Text-secondary, not the quiet tier: this line is the reason to
          distrust the counts above it, so it has to survive a glance. */}
      <p className="text-[10px] text-slate-400">
        Evidence: MAC table {agoLabel(s.macs_collected_at)} · Device names{" "}
        {agoLabel(s.devices_resolved_at)} · PPPoE live sessions{" "}
        {agoLabel(s.pppoe_observed_at)}.{" "}
        {noLiveProof
          ? "Devices were resolved from the Gigafy portal, which reports no live sessions — live customers cannot be ruled out."
          : "Live-session data older than a day cannot prove a line is idle."}
      </p>
    </div>
  );
}

function BulkPonClearForm({
  chassisId,
  prefill,
}: {
  chassisId: string;
  prefill?: ManualOpPrefill;
}) {
  const [port, setPort] = useState(prefill?.port ?? "");
  const [preview, setPreview] = useState<BulkClearPreview | null>(null);
  const { submit, confirmDialog } = useSubmitDestructive(chassisId);
  const toast = useToast();
  const { ports } = useInventory(chassisId);

  // The bulk_pon_clear arm needs an explicit ``targets`` list — a bare
  // port resolves to nothing and the job fails "requires non-empty
  // targets". So we resolve port → ONUs via the preview endpoint
  // (reads onu_inventory, no SSH) and submit those targets verbatim.
  const previewMut = useMutation({
    mutationFn: async () => {
      const res = await api.post<BulkClearPreview>(
        `/chassis/${chassisId}/maintenance/bulk-pon-clear/preview`,
        { scope: "port", port },
      );
      return res.data;
    },
    onSuccess: (data) => setPreview(data),
    onError: () => toast.push("Preview failed — check the port", "error"),
  });

  const run = async () => {
    if (!preview || preview.onus.length === 0) return;
    const targets = preview.onus.map((o) => ({
      port: o.port,
      display_id: o.display_id,
    }));
    // Use the server's confirm string verbatim so the dialog summary
    // matches the scope the operator actually previewed.
    const queued = await submit(
      "bulk_pon_clear",
      { targets },
      preview.confirm_expected,
      preview.rendered_block || null,
    );
    if (queued) setPreview(null);
  };

  // Stale the preview if the operator changes the port after previewing.
  const onPortChange = (v: string) => {
    setPort(v);
    setPreview(null);
  };

  return (
    <div className="grid gap-3 sm:grid-cols-2">
      <Field label="Port" hint="from inventory; ALL ONUs on it are removed">
        <SelectOrCustom
          value={port}
          onChange={onPortChange}
          options={ports}
          placeholder="Select a PON port…"
          className={inputCls}
        />
      </Field>
      <div className="flex items-end">
        <button
          type="button"
          onClick={() => previewMut.mutate()}
          disabled={previewMut.isPending || !port}
          className="inline-flex items-center gap-1 rounded border border-slate-700 bg-slate-800 px-3 py-1.5 text-xs text-slate-200 hover:bg-slate-700 disabled:opacity-50"
        >
          <FileSearch className="h-3 w-3" />
          {previewMut.isPending ? "Resolving…" : "Preview ONUs"}
        </button>
      </div>

      {preview && (
        // Borderless wrapper: the impact panel, the table and the CLI
        // block each carry their own shadow-border ring, so a box round
        // the outside would be a box inside a box.
        <div className="sm:col-span-2 space-y-2">
          <p className="text-[11px] text-slate-400">
            {preview.onus.length === 0 ? (
              <span className="text-amber-300">
                No ONUs found on port “{port}”. Nothing to clear — check
                the port value (it must match inventory exactly).
              </span>
            ) : (
              <>
                Will wipe{" "}
                <strong className="text-red-300">
                  {preview.onus.length}
                </strong>{" "}
                ONU{preview.onus.length === 1 ? "" : "s"} on port {port}:
              </>
            )}
          </p>
          {preview.onus.length > 0 && (
            <>
              {preview.service_summary && (
                <BulkClearImpact s={preview.service_summary} />
              )}
              {/* Table, not chips: the operator is comparing service
                  context down a column, which is exactly what chips
                  make impossible. */}
              <div className="max-h-72 overflow-y-auto rounded-lg bg-slate-900 shadow-border">
                <table className="w-full text-left text-[11px]">
                  <thead className="sticky top-0 bg-slate-900 text-[10px] uppercase tracking-wide text-slate-400">
                    <tr>
                      <th className="px-2 py-1.5 font-medium">ONU</th>
                      <th className="px-2 py-1.5 font-medium">Serial</th>
                      <th className="px-2 py-1.5 font-medium">VNET</th>
                      <th className="px-2 py-1.5 font-medium">Gigafy device</th>
                      <th className="px-2 py-1.5 font-medium">Impact</th>
                    </tr>
                  </thead>
                  <tbody>
                    {preview.onus.map((o) => {
                      const impact = impactOf(o);
                      return (
                        <tr
                          key={`${o.port}/${o.display_id}`}
                          className="border-t border-slate-800/60"
                        >
                          <td className="px-2 py-1 font-mono text-slate-200">
                            {o.display_id}
                          </td>
                          <td className="px-2 py-1 font-mono text-slate-400">
                            {o.serial ?? "—"}
                          </td>
                          <td className="px-2 py-1 font-mono text-slate-400">
                            {o.vnets?.length ? o.vnets.join(", ") : "—"}
                          </td>
                          <td className="px-2 py-1 font-mono text-slate-300">
                            {o.gigafy_devices?.length
                              ? o.gigafy_devices.join(", ")
                              : "—"}
                          </td>
                          <td className="px-2 py-1">
                            <Badge tone={IMPACT_TONE[impact]}>
                              {IMPACT_LABEL[impact]}
                            </Badge>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </>
          )}
          {preview.rendered_block && (
            <CommandPreview text={preview.rendered_block} />
          )}
        </div>
      )}

      <div className="sm:col-span-2">
        <button
          type="button"
          onClick={run}
          disabled={!preview || preview.onus.length === 0}
          className="inline-flex items-center gap-1 rounded border border-red-800 bg-red-900/30 px-3 py-1.5 text-xs text-red-200 hover:bg-red-900/50 disabled:opacity-50"
        >
          <Play className="h-3 w-3" />
          Submit bulk clear (destructive)
        </button>
        <p className="mt-1 text-[10px] text-slate-500">
          Preview first to resolve the port into its ONUs. Wipes every
          ONU on the chosen PON; restore via re-provisioning from backup.
        </p>
      </div>
      {confirmDialog}
    </div>
  );
}

// ----------------------------------------------------------------------
// Replace VNET
// ----------------------------------------------------------------------

type ReplaceVnetTarget = {
  display_id: string;
  port: string;
  serial: string | null;
  bandwidth_profile: string;
  shaper_profile: string;
  cosq_profile: string;
};
type ReplaceVnetPreview = {
  old_vnet: string;
  new_vnet: string;
  scope: string;
  targets: ReplaceVnetTarget[];
  rendered_block: string;
};

function ReplaceVnetForm({
  chassisId,
  prefill,
}: {
  chassisId: string;
  prefill?: ManualOpPrefill;
}) {
  const [port, setPort] = useState(prefill?.port ?? "");
  const [displayId, setDisplayId] = useState(prefill?.displayId ?? "");
  const [oldVnet, setOldVnet] = useState("");
  const [newVnet, setNewVnet] = useState("");
  const [preview, setPreview] = useState<ReplaceVnetPreview | null>(null);
  const { submit, confirmDialog } = useSubmitDestructive(chassisId);
  const toast = useToast();
  const { ports, displayIdsForPort } = useInventory(chassisId);

  // VNET options for the old/new dropdowns (parsed config profiles).
  const profilesQ = useQuery<ProfileListResponse>({
    queryKey: ["v2", "chassis", chassisId, "profiles"],
    queryFn: async () => {
      const res = await api.get<ProfileListResponse>(
        `/chassis/${chassisId}/profiles`,
      );
      return res.data;
    },
  });
  const knownVnet = Array.from(
    new Set(
      (profilesQ.data?.profiles ?? [])
        .filter((p) => p.category === "vnet")
        .map((p) => p.name),
    ),
  ).sort();

  // The replace_vnet arm needs a resolved ``targets`` list (each carries
  // the ONU's EXISTING bandwidth/shaper/cosq so the migration preserves
  // them). The preview endpoint builds it from onu_inventory + the
  // latest backup — submitting without it fails "requires … targets".
  const previewMut = useMutation({
    mutationFn: async () => {
      const res = await api.post<ReplaceVnetPreview>(
        `/chassis/${chassisId}/maintenance/replace-vnet/preview`,
        {
          scope: "onu",
          display_id: displayId,
          old_vnet: oldVnet,
          new_vnet: newVnet,
        },
      );
      return res.data;
    },
    onSuccess: (d) => setPreview(d),
    onError: () =>
      toast.push("Preview failed — check the ONU + VNET names", "error"),
  });

  const stale = () => setPreview(null);

  const run = async () => {
    if (!preview || preview.targets.length === 0) return;
    const summary = `replace ${displayId} ${oldVnet} -> ${newVnet}`;
    const queued = await submit(
      "replace_vnet",
      { old_vnet: oldVnet, new_vnet: newVnet, targets: preview.targets },
      summary,
      preview.rendered_block || null,
    );
    if (queued) setPreview(null);
  };

  const canPreview =
    !!displayId && !!oldVnet && !!newVnet && oldVnet !== newVnet;

  return (
    <div className="grid gap-3 sm:grid-cols-2">
      <Field label="Port" hint="from inventory">
        <SelectOrCustom
          value={port}
          onChange={(v) => {
            setPort(v);
            setDisplayId("");
            stale();
          }}
          options={ports}
          placeholder="Select a PON port…"
          className={inputCls}
        />
      </Field>
      <Field label="Display ID" hint={port ? "ONUs on this port" : "pick a port first"}>
        <SelectOrCustom
          value={displayId}
          onChange={(v) => {
            setDisplayId(v);
            stale();
          }}
          options={displayIdsForPort(port)}
          placeholder="Select a display ID…"
          className={inputCls}
        />
      </Field>
      <Field label="Old VNET">
        <SelectOrCustom
          value={oldVnet}
          onChange={(v) => {
            setOldVnet(v);
            stale();
          }}
          options={knownVnet}
          placeholder="Current VNET…"
          className={inputCls}
        />
      </Field>
      <Field label="New VNET" hint="must differ from old VNET">
        <SelectOrCustom
          value={newVnet}
          onChange={(v) => {
            setNewVnet(v);
            stale();
          }}
          options={knownVnet}
          placeholder="Target VNET…"
          className={inputCls}
        />
      </Field>

      <div className="sm:col-span-2 flex items-center gap-2">
        <button
          type="button"
          onClick={() => previewMut.mutate()}
          disabled={previewMut.isPending || !canPreview}
          className="inline-flex items-center gap-1 rounded border border-slate-700 bg-slate-800 px-3 py-1.5 text-xs text-slate-200 hover:bg-slate-700 disabled:opacity-50"
        >
          <FileSearch className="h-3 w-3" />
          {previewMut.isPending ? "Resolving…" : "Preview"}
        </button>
        <button
          type="button"
          onClick={run}
          disabled={!preview || preview.targets.length === 0}
          className="inline-flex items-center gap-1 rounded border border-red-800 bg-red-900/30 px-3 py-1.5 text-xs text-red-200 hover:bg-red-900/50 disabled:opacity-50"
        >
          <Play className="h-3 w-3" />
          Submit VNET replace (destructive)
        </button>
      </div>

      {preview && (
        <div className="sm:col-span-2 space-y-1 rounded border border-slate-800 bg-slate-950/40 p-2 text-[11px]">
          {preview.targets.length === 0 ? (
            <span className="text-amber-300">
              No service matched display {displayId} on VNET “{oldVnet}”.
              Check the display ID + old VNET against the current config.
            </span>
          ) : (
            <>
              <p className="text-slate-400">
                Will migrate{" "}
                <strong className="text-emerald-300">
                  {preview.targets.length}
                </strong>{" "}
                service{preview.targets.length === 1 ? "" : "s"} ({oldVnet} →{" "}
                {newVnet}); existing bandwidth/shaper/cosq preserved:
              </p>
              <div className="flex flex-wrap gap-1">
                {preview.targets.map((t) => (
                  <Badge key={t.display_id} tone="muted">
                    {t.display_id} · {t.bandwidth_profile}/{t.shaper_profile}
                  </Badge>
                ))}
              </div>
              {preview.rendered_block && (
                <CommandPreview text={preview.rendered_block} />
              )}
            </>
          )}
        </div>
      )}
      {confirmDialog}
    </div>
  );
}

// ----------------------------------------------------------------------
// Add service — append ONE extra VNET service to already-provisioned
// ONUs without touching their existing services.
// ----------------------------------------------------------------------

type AddServiceScope = "onu" | "port" | "chassis";

const ADD_SERVICE_SCOPES: { value: AddServiceScope; label: string }[] = [
  { value: "onu", label: "Single ONU" },
  { value: "port", label: "PON port" },
  { value: "chassis", label: "Whole chassis" },
];

/** Preferred default for both upstream bandwidth + downstream shaper. */
const ADD_SERVICE_DEFAULT_RATE = "10M-1M-commit";
const BACKUP_STALE_MS = 24 * 60 * 60 * 1000;

type AddServiceCounts = {
  to_add: number;
  already_has: number;
  no_services: number;
  down: number;
  not_in_backup: number;
};
type AddServicePreview = {
  backup_id: number | null;
  backup_taken_at: string | null;
  scope: AddServiceScope;
  vnet_profile: string;
  bandwidth_profile: string;
  shaper_profile: string;
  cosq_profile: string;
  counts: AddServiceCounts;
  ports: {
    port: string;
    to_add: number;
    already_has: number;
    no_services: number;
    down: number;
  }[];
  targets: {
    port: string;
    display_id: string;
    serial: string | null;
    state: string | null;
  }[];
  missing_profiles: string[];
  cli_preview: string;
};
type AddServiceSubmitResponse = {
  job_id: string;
  kind: "add_onu_service" | "add_service_from_scope";
};

function isAddServiceScope(v: string | undefined): v is AddServiceScope {
  return v === "onu" || v === "port" || v === "chassis";
}

/** Most-used profile name in a category (ties → alphabetical). */
function mostUsedProfile(profiles: Profile[], cat: string): string | null {
  const list = profiles.filter((p) => p.category === cat);
  if (list.length === 0) return null;
  return [...list].sort(
    (a, b) => b.usage_count - a.usage_count || a.name.localeCompare(b.name),
  )[0].name;
}

function AddServiceForm({
  chassisId,
  prefill,
}: {
  chassisId: string;
  prefill?: ManualOpPrefill;
}) {
  const [scope, setScope] = useState<AddServiceScope>(() => {
    const s = prefill?.scope;
    if (isAddServiceScope(s)) return s;
    if (prefill?.displayId) return "onu";
    if (prefill?.port) return "port";
    return "onu";
  });
  const [port, setPort] = useState(prefill?.port ?? "");
  const [displayId, setDisplayId] = useState(prefill?.displayId ?? "");
  const [vnet, setVnet] = useState("");
  const [bandwidth, setBandwidth] = useState("");
  const [shaper, setShaper] = useState("");
  const [cosq, setCosq] = useState("");
  const [includeDown, setIncludeDown] = useState(false);
  const [batchSize, setBatchSize] = useState(5);
  const [updateSiteDefaults, setUpdateSiteDefaults] = useState(false);
  const [typedConfirm, setTypedConfirm] = useState("");
  const [preview, setPreview] = useState<AddServicePreview | null>(null);
  const [submitted, setSubmitted] = useState<AddServiceSubmitResponse | null>(
    null,
  );
  const [submitting, setSubmitting] = useState(false);
  const { confirm, confirmDialog } = useConfirm();
  const toast = useToast();
  const { ports, displayIdsForPort } = useInventory(chassisId);

  // Chassis name for the whole-chassis typed confirm — same query key as
  // ChassisDetail, so this is served from its cache entry.
  const chassisQ = useQuery<{
    gigafy_device_name: string | null;
    effective_device_name: string;
  }>({
    queryKey: ["v2", "chassis", chassisId],
    queryFn: async () => {
      const res = await api.get(`/chassis/${chassisId}`);
      return res.data;
    },
  });
  const chassisName =
    chassisQ.data?.gigafy_device_name ??
    chassisQ.data?.effective_device_name ??
    `chassis-${chassisId}`;

  const profilesQ = useQuery<ProfileListResponse>({
    queryKey: ["v2", "chassis", chassisId, "profiles"],
    queryFn: async () => {
      const res = await api.get<ProfileListResponse>(
        `/chassis/${chassisId}/profiles`,
      );
      return res.data;
    },
  });
  const allProfiles = useMemo(
    () => profilesQ.data?.profiles ?? [],
    [profilesQ.data],
  );
  const known = useMemo<KnownProfiles>(() => {
    const byCat = (cat: string) =>
      Array.from(
        new Set(
          allProfiles.filter((p) => p.category === cat).map((p) => p.name),
        ),
      ).sort();
    return {
      vnet: byCat("vnet"),
      bandwidth: byCat("bandwidth"),
      shaper: byCat("shaper"),
      cosq: byCat("cosq"),
    };
  }, [allProfiles]);
  const vnetUsage = useMemo(() => {
    const m = new Map<string, number>();
    for (const p of allProfiles) {
      if (p.category !== "vnet") continue;
      m.set(p.name, Math.max(m.get(p.name) ?? 0, p.usage_count));
    }
    return m;
  }, [allProfiles]);
  const unusedVnets = known.vnet.filter((n) => (vnetUsage.get(n) ?? 0) === 0);

  // Seed the rate / cosq defaults once the parsed profiles land:
  // 10M-1M-commit when the OLT has it (else the most-used profile),
  // cosq = the most-used cosq (never a hardcoded c0).
  useEffect(() => {
    if (!profilesQ.data) return;
    const pick = (cat: "bandwidth" | "shaper") =>
      known[cat].includes(ADD_SERVICE_DEFAULT_RATE)
        ? ADD_SERVICE_DEFAULT_RATE
        : (mostUsedProfile(allProfiles, cat) ?? "");
    setBandwidth((v) => v || pick("bandwidth"));
    setShaper((v) => v || pick("shaper"));
    setCosq((v) => v || (mostUsedProfile(allProfiles, "cosq") ?? ""));
  }, [profilesQ.data, known, allProfiles]);

  const stale = () => setPreview(null);
  const withStale = (setter: (v: string) => void) => (v: string) => {
    setter(v);
    stale();
  };

  const scopeReady =
    scope === "chassis" ||
    (scope === "port" && !!port) ||
    (scope === "onu" && !!port && !!displayId);
  const canPreview =
    scopeReady && !!vnet && !!bandwidth && !!shaper && !profilesQ.isLoading;

  const requestBody = () => ({
    scope,
    port: scope === "chassis" ? null : port,
    display_id: scope === "onu" ? displayId : null,
    vnet_profile: vnet,
    bandwidth_profile: bandwidth,
    shaper_profile: shaper,
    cosq_profile: cosq.trim() || null,
    include_down: includeDown,
  });

  const previewMut = useMutation({
    mutationFn: async () => {
      const res = await api.post<AddServicePreview>(
        `/chassis/${chassisId}/maintenance/add-service/preview`,
        requestBody(),
      );
      return res.data;
    },
    onSuccess: (d) => setPreview(d),
    onError: (err) =>
      toast.push(
        apiErrorMessage(err, "Preview failed — check the inputs"),
        "error",
      ),
  });

  const expectedConfirm = `add ${vnet} to ${chassisName}`;
  const missing = preview?.missing_profiles ?? [];
  const toAdd = preview?.counts.to_add ?? 0;
  const canSubmit =
    !!preview &&
    toAdd > 0 &&
    missing.length === 0 &&
    !submitting &&
    (scope !== "chassis" || typedConfirm.trim() === expectedConfirm);

  const scopeLabel =
    scope === "onu"
      ? `ONU ${port}/${displayId}`
      : scope === "port"
        ? `PON port ${port}`
        : `every PON port on ${chassisName}`;
  const cliLabel =
    scope === "onu" ? "Commands to run" : "Commands (first target)";

  const run = async () => {
    if (!preview || !canSubmit) return;
    const lines = [
      `add ${vnet} (US ${bandwidth} / DS ${shaper} / cosq ${preview.cosq_profile})`,
      `to ${toAdd} ONU${toAdd === 1 ? "" : "s"} on ${scopeLabel}`,
      "existing services are left untouched",
    ];
    if (scope !== "onu") {
      lines.push(
        `fresh backup first, then batches of ${batchSize} run one after another`,
      );
    }
    if (scope === "chassis" && updateSiteDefaults) {
      lines.push(
        "also appends this service to the site auto-provision defaults",
      );
    }
    const ok = await confirm({
      title:
        scope === "onu"
          ? "Confirm add_onu_service"
          : "Confirm add_service_from_scope",
      summary: lines.join("\n"),
      children: preview.cli_preview ? (
        <CommandPreview text={preview.cli_preview} label={cliLabel} />
      ) : undefined,
      confirmLabel: "Submit",
    });
    if (!ok) return;
    setSubmitting(true);
    try {
      const res = await api.post<AddServiceSubmitResponse>(
        `/chassis/${chassisId}/maintenance/add-service`,
        {
          ...requestBody(),
          batch_size: scope === "onu" ? null : batchSize,
          update_site_defaults:
            scope === "chassis" ? updateSiteDefaults : false,
          confirm: scope === "chassis" ? typedConfirm.trim() : null,
        },
      );
      window.dispatchEvent(
        new CustomEvent("inline-job-tracker.add", {
          detail: { jobId: res.data.job_id, kind: res.data.kind },
        }),
      );
      toast.push(`${res.data.kind} queued`, "success");
      setSubmitted(res.data);
      setPreview(null);
      setTypedConfirm("");
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error("add-service submit failed", err);
      toast.push(
        apiErrorMessage(err, "Submit failed — check the form"),
        "error",
      );
    } finally {
      setSubmitting(false);
    }
  };

  const backupAgeMs = preview?.backup_taken_at
    ? Date.now() - new Date(preview.backup_taken_at).getTime()
    : null;
  const backupStale = backupAgeMs == null || backupAgeMs > BACKUP_STALE_MS;

  return (
    <div className="grid gap-3 sm:grid-cols-2">
      <div className="sm:col-span-2 flex flex-wrap items-center gap-2">
        <div
          role="radiogroup"
          aria-label="Scope"
          className="inline-flex rounded border border-slate-700 bg-slate-950 p-0.5"
        >
          {ADD_SERVICE_SCOPES.map((s) => (
            <button
              key={s.value}
              type="button"
              role="radio"
              aria-checked={scope === s.value}
              onClick={() => {
                setScope(s.value);
                setTypedConfirm("");
                stale();
              }}
              className={
                "rounded px-3 py-1 text-xs transition-[background-color,color] duration-150 ease-out " +
                (scope === s.value
                  ? "bg-slate-700 text-slate-100"
                  : "text-slate-400 hover:text-slate-200")
              }
            >
              {s.label}
            </button>
          ))}
        </div>
        <HelpButton topic="chassis-add-service" />
        <span className="text-[10px] text-slate-500">
          Appends one service; existing services are never removed.
        </span>
      </div>

      {scope !== "chassis" && (
        <Field label="Port" hint="from inventory">
          <SelectOrCustom
            value={port}
            onChange={(v) => {
              setPort(v);
              setDisplayId("");
              stale();
            }}
            options={ports}
            placeholder="Select a PON port…"
            className={inputCls}
          />
        </Field>
      )}
      {scope === "onu" && (
        <Field
          label="Display ID"
          hint={port ? "ONUs on this port" : "pick a port first"}
        >
          <SelectOrCustom
            value={displayId}
            onChange={withStale(setDisplayId)}
            options={displayIdsForPort(port)}
            placeholder="Select a display ID…"
            className={inputCls}
          />
        </Field>
      )}

      <div className="sm:col-span-2 space-y-2 rounded border border-slate-800/80 bg-slate-900/40 p-2">
        <div className="flex items-baseline justify-between text-[11px]">
          <span className="font-semibold text-slate-300">Service to add</span>
          {profilesQ.data && known.vnet.length === 0 && (
            <span className="text-[10px] text-amber-300">
              no parsed profiles — take a backup + re-parse for dropdowns
            </span>
          )}
        </div>
        <div className="grid gap-2 sm:grid-cols-2">
          <Field
            label="VNET profile"
            hint={
              unusedVnets.length > 0
                ? `Unused on this OLT: ${unusedVnets.join(", ")}`
                : undefined
            }
          >
            <SelectOrCustom
              value={vnet}
              onChange={(v) => {
                setVnet(v);
                setTypedConfirm("");
                stale();
              }}
              options={known.vnet}
              optionLabel={(n) => {
                const used = vnetUsage.get(n) ?? 0;
                return used === 0 ? `${n} — unused` : `${n} (${used} in use)`;
              }}
              placeholder="Pick a VNET…"
              className={inputCls}
            />
            {notOnOlt(vnet, known.vnet) && (
              <span className="text-[10px] text-amber-300">
                “{vnet}” (not on OLT)
              </span>
            )}
          </Field>
          <Field label="Upstream (bandwidth)">
            <SelectOrCustom
              value={bandwidth}
              onChange={withStale(setBandwidth)}
              options={known.bandwidth}
              placeholder="Pick a bandwidth profile…"
              className={inputCls}
            />
            {notOnOlt(bandwidth, known.bandwidth) && (
              <span className="text-[10px] text-amber-300">
                “{bandwidth}” (not on OLT)
              </span>
            )}
          </Field>
          <Field label="Downstream (shaper)">
            <SelectOrCustom
              value={shaper}
              onChange={withStale(setShaper)}
              options={known.shaper}
              placeholder="Pick a shaper profile…"
              className={inputCls}
            />
            {notOnOlt(shaper, known.shaper) && (
              <span className="text-[10px] text-amber-300">
                “{shaper}” (not on OLT)
              </span>
            )}
          </Field>
          <Field label="COS profile (cosq)" hint="defaults to the most-used cosq">
            <SelectOrCustom
              value={cosq}
              onChange={withStale(setCosq)}
              options={known.cosq}
              placeholder="Pick a cosq profile…"
              className={inputCls}
            />
            {notOnOlt(cosq, known.cosq) && (
              <span className="text-[10px] text-amber-300">
                “{cosq}” (not on OLT)
              </span>
            )}
          </Field>
        </div>
      </div>

      <div className="sm:col-span-2 flex flex-col gap-1.5 text-xs text-slate-300">
        <label className="inline-flex items-center gap-2">
          <input
            type="checkbox"
            checked={includeDown}
            onChange={(e) => {
              setIncludeDown(e.target.checked);
              stale();
            }}
            className="accent-emerald-600"
          />
          Include down ONUs
          <span className="text-[10px] text-slate-500">
            (off: ONUs that aren&apos;t up are counted but skipped)
          </span>
        </label>
        {scope === "chassis" && (
          <label className="inline-flex items-center gap-2">
            <input
              type="checkbox"
              checked={updateSiteDefaults}
              onChange={(e) => setUpdateSiteDefaults(e.target.checked)}
              className="accent-emerald-600"
            />
            Also add to site auto-provision defaults
            <span className="text-[10px] text-slate-500">
              (newly provisioned ONUs at this site get the service too)
            </span>
          </label>
        )}
        {scope !== "onu" && (
          <details className="text-[11px] text-slate-400">
            <summary className="cursor-pointer select-none text-slate-500 hover:text-slate-300">
              Advanced
            </summary>
            <label className="mt-1.5 inline-flex items-center gap-2">
              Batch size
              <input
                type="number"
                min={1}
                max={10}
                value={batchSize}
                onChange={(e) =>
                  setBatchSize(
                    Math.min(
                      10,
                      Math.max(1, parseInt(e.target.value, 10) || 5),
                    ),
                  )
                }
                className="w-16 rounded border border-slate-700 bg-slate-950 px-2 py-0.5 text-xs text-slate-100 focus-visible:border-emerald-700 focus-visible:outline-none"
              />
              <span className="text-[10px] text-slate-500">
                ONUs per job (1–10); batches run one after another
              </span>
            </label>
          </details>
        )}
      </div>

      <div className="sm:col-span-2 flex items-center gap-2">
        <button
          type="button"
          onClick={() => previewMut.mutate()}
          disabled={previewMut.isPending || !canPreview}
          className="inline-flex items-center gap-1 rounded border border-slate-700 bg-slate-800 px-3 py-1.5 text-xs text-slate-200 hover:bg-slate-700 disabled:opacity-50"
        >
          <FileSearch className="h-3 w-3" />
          {previewMut.isPending ? "Resolving…" : "Preview"}
        </button>
      </div>

      {preview && (
        <div className="sm:col-span-2 space-y-2 rounded border border-slate-800 bg-slate-950/40 p-2 text-[11px]">
          <p className={backupStale ? "text-amber-300" : "text-slate-400"}>
            {preview.backup_id == null ? (
              <>No backup parsed yet — counts are unknown.</>
            ) : (
              <>
                From backup #{preview.backup_id} taken{" "}
                <LocalTime iso={preview.backup_taken_at} /> (
                {agoLabel(preview.backup_taken_at)}).
              </>
            )}
            {backupStale && (
              <>
                {" "}
                Older than 24h: port/chassis runs take a fresh backup first;
                single-ONU uses this backup.
              </>
            )}
          </p>

          <div className="flex flex-wrap items-center gap-1.5">
            <Badge tone={toAdd > 0 ? "success" : "muted"}>{toAdd} to add</Badge>
            <Badge tone="muted">
              {preview.counts.already_has} already have it
            </Badge>
            <Badge tone={preview.counts.no_services > 0 ? "warning" : "muted"}>
              {preview.counts.no_services} no services
            </Badge>
            <Badge
              tone={
                preview.counts.down > 0 && !includeDown ? "warning" : "muted"
              }
            >
              {preview.counts.down} down
              {includeDown ? " (included)" : " (skipped)"}
            </Badge>
            <Badge
              tone={preview.counts.not_in_backup > 0 ? "warning" : "muted"}
            >
              {preview.counts.not_in_backup} not in backup
            </Badge>
          </div>

          {missing.length > 0 && (
            <div className="rounded border border-red-800 bg-red-950/40 p-2 text-red-200">
              <p className="font-medium">
                Missing on the OLT — submit is blocked:
              </p>
              <ul className="mt-0.5 list-disc pl-4 font-mono">
                {missing.map((m) => (
                  <li key={m}>{m}</li>
                ))}
              </ul>
            </div>
          )}

          {scope !== "onu" && preview.ports.length > 0 && (
            <div className="max-h-64 overflow-y-auto rounded-lg bg-slate-900 shadow-border">
              <table className="w-full text-left text-[11px]">
                <thead className="sticky top-0 bg-slate-900 text-[10px] uppercase tracking-wide text-slate-400">
                  <tr>
                    <th className="px-2 py-1.5 font-medium">Port</th>
                    <th className="px-2 py-1.5 text-right font-medium">
                      To add
                    </th>
                    <th className="px-2 py-1.5 text-right font-medium">
                      Already has
                    </th>
                    <th className="px-2 py-1.5 text-right font-medium">
                      No services
                    </th>
                    <th className="px-2 py-1.5 text-right font-medium">Down</th>
                  </tr>
                </thead>
                <tbody className="font-mono">
                  {preview.ports.map((p) => (
                    <tr key={p.port} className="border-t border-slate-800/60">
                      <td className="px-2 py-1 text-slate-200">{p.port}</td>
                      <td className="px-2 py-1 text-right text-emerald-300">
                        {p.to_add}
                      </td>
                      <td className="px-2 py-1 text-right text-slate-400">
                        {p.already_has}
                      </td>
                      <td className="px-2 py-1 text-right text-slate-400">
                        {p.no_services}
                      </td>
                      <td className="px-2 py-1 text-right text-slate-400">
                        {p.down}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {preview.targets.length > 0 && (
            <details open={scope === "onu"}>
              <summary className="cursor-pointer text-slate-400">
                {preview.targets.length} target
                {preview.targets.length === 1 ? "" : "s"}
              </summary>
              <div className="mt-1 flex max-h-32 flex-wrap gap-1 overflow-y-auto">
                {preview.targets.map((t) => (
                  <Badge
                    key={`${t.port}/${t.display_id}`}
                    tone="muted"
                    title={
                      [t.serial, t.state].filter(Boolean).join(" · ") ||
                      undefined
                    }
                  >
                    {t.port}/{t.display_id}
                  </Badge>
                ))}
              </div>
            </details>
          )}

          {toAdd === 0 && missing.length === 0 && (
            <p className="text-amber-300">
              Nothing to add — every ONU in scope already has {vnet}, has no
              services, or is skipped.
            </p>
          )}

          {preview.cli_preview && (
            <CommandPreview text={preview.cli_preview} label={cliLabel} />
          )}
        </div>
      )}

      {scope === "chassis" && preview && toAdd > 0 && missing.length === 0 && (
        <div className="sm:col-span-2">
          <Field
            label="Type to confirm the whole-chassis run"
            hint={`Type exactly: ${expectedConfirm}`}
          >
            <input
              type="text"
              value={typedConfirm}
              onChange={(e) => setTypedConfirm(e.target.value)}
              placeholder={expectedConfirm}
              spellCheck={false}
              autoComplete="off"
              className={inputCls + " font-mono"}
            />
          </Field>
        </div>
      )}

      <div className="sm:col-span-2">
        <button
          type="button"
          onClick={run}
          disabled={!canSubmit}
          className="inline-flex items-center gap-1 rounded border border-red-800 bg-red-900/30 px-3 py-1.5 text-xs text-red-200 hover:bg-red-900/50 disabled:opacity-50"
        >
          <Play className="h-3 w-3" />
          {submitting
            ? "Submitting…"
            : preview
              ? `Add ${vnet || "service"} to ${toAdd} ONU${toAdd === 1 ? "" : "s"}`
              : "Add service"}
        </button>
        <p className="mt-1 text-[10px] text-slate-500">
          Preview first. Port and chassis runs take a fresh backup, then add
          the service in batches of {batchSize}, one batch at a time.
        </p>
      </div>

      {submitted?.kind === "add_service_from_scope" && (
        <div className="sm:col-span-2">
          <AddServiceBatches
            parentId={submitted.job_id}
            onDismiss={() => setSubmitted(null)}
          />
        </div>
      )}
      {confirmDialog}
    </div>
  );
}

const BATCH_TERMINAL = new Set(["succeeded", "failed", "cancelled", "partial"]);

/** Live batch progress for an ``add_service_from_scope`` arm job — its
 *  child ``add_onu_service`` jobs hang off it via parent_job_id and are
 *  listed by GET /bulk-jobs/{parentId}, same as a bulk fan-out. */
function AddServiceBatches({
  parentId,
  onDismiss,
}: {
  parentId: string;
  onDismiss: () => void;
}) {
  const q = useQuery<BulkJobDetailView>({
    queryKey: ["v2", "bulk-jobs", parentId],
    queryFn: async () => {
      const res = await api.get<BulkJobDetailWire>(`/bulk-jobs/${parentId}`);
      return normaliseBulkJobDetail(res.data);
    },
    refetchInterval: (query) => {
      const s = query.state.data?.parent?.status;
      return s && BATCH_TERMINAL.has(s) ? false : 5_000;
    },
  });
  const parent = q.data?.parent;
  const rows = q.data?.children ?? [];
  const done = rows.filter((c) => BATCH_TERMINAL.has(c.status)).length;
  return (
    <section className="rounded-lg bg-slate-900 p-3 shadow-border">
      <div className="mb-2 flex flex-wrap items-center gap-2 text-xs">
        <span className="font-medium text-slate-200">Add-service batches</span>
        {parent && (
          <Badge tone={CHILD_JOB_STATUS_TONE[parent.status] ?? "neutral"}>
            {parent.status}
          </Badge>
        )}
        <span className="font-mono text-[11px] text-slate-400">
          {done}/{rows.length} batches done
        </span>
        <span className="flex-1" />
        <button
          type="button"
          onClick={onDismiss}
          className="text-[11px] text-slate-500 hover:text-slate-200"
        >
          hide
        </button>
      </div>
      {q.isLoading ? (
        <p className="text-xs text-slate-500">Loading…</p>
      ) : q.isError ? (
        <p className="text-xs text-amber-300">
          Couldn&apos;t load batch progress — the inline tracker below still
          follows the parent job.
        </p>
      ) : rows.length === 0 ? (
        <p className="text-xs text-slate-500">
          {parent && BATCH_TERMINAL.has(parent.status)
            ? "No batches were created — check the job's results."
            : "Taking a fresh backup and resolving targets — batches appear here once queued."}
        </p>
      ) : (
        <ChildJobsTable rows={rows} firstColumn="batch" />
      )}
    </section>
  );
}

// ----------------------------------------------------------------------
// Provision ONU
// ----------------------------------------------------------------------

function ProvisionOnuForm({
  chassisId,
  prefill,
}: {
  chassisId: string;
  prefill?: ManualOpPrefill;
}) {
  const [port, setPort] = useState(prefill?.port ?? "");
  const [displayId, setDisplayId] = useState("");
  const [onuId, setOnuId] = useState("");
  const [serial, setSerial] = useState(prefill?.serial ?? "");
  const [profile, setProfile] = useState("");
  const [vnet, setVnet] = useState("");
  const [preview, setPreview] = useState<string | null>(null);
  const { submit, confirmDialog } = useSubmitDestructive(chassisId);
  const toast = useToast();

  // The direct provision form submits the base ONU only (service VNET
  // blocks come from the Pending flow), so the preview renders with no
  // services — matching exactly what this form's job would send.
  const previewMut = useMutation({
    mutationFn: async () => {
      const res = await api.post<CliPreview>(
        `/chassis/${chassisId}/maintenance/provision-onu/preview`,
        {
          port,
          display_id: displayId,
          serial: serial.trim(),
          onu_id: onuId.trim() ? parseInt(onuId, 10) : null,
          services: [],
        },
      );
      return res.data;
    },
    onSuccess: (d) => setPreview(d.rendered_block),
    onError: () => toast.push("Preview failed — check the inputs", "error"),
  });

  // Known-or-custom dropdowns: pull the chassis's parsed profiles so the
  // operator picks from real bandwidth / VNET profile names (with a
  // Custom… escape hatch for values the parser hasn't seen yet).
  const profilesQ = useQuery<ProfileListResponse>({
    queryKey: ["v2", "chassis", chassisId, "profiles"],
    queryFn: async () => {
      const res = await api.get<ProfileListResponse>(
        `/chassis/${chassisId}/profiles`,
      );
      return res.data;
    },
  });
  const profileNames = (cat: string): string[] =>
    Array.from(
      new Set(
        (profilesQ.data?.profiles ?? [])
          .filter((p) => p.category === cat)
          .map((p) => p.name),
      ),
    ).sort();
  const knownBandwidth = profileNames("bandwidth");
  const knownVnet = profileNames("vnet");

  const run = async () => {
    const summary = `provision ${port}/${displayId} ${serial}`;
    const queued = await submit(
      "provision_onu",
      {
        port,
        display_id: displayId,
        onu_id: parseInt(onuId, 10) || null,
        serial: serial.trim(),
        profile: profile.trim() || null,
        vnet_or_vlan: vnet.trim() || null,
      },
      summary,
      preview,
    );
    if (queued) setPreview(null);
  };

  return (
    <div className="grid gap-3 sm:grid-cols-2">
      <Field label="Port">
        <input
          type="text"
          value={port}
          onChange={(e) => setPort(e.target.value)}
          className={inputCls}
        />
      </Field>
      <Field label="Display ID">
        <input
          type="text"
          value={displayId}
          onChange={(e) => setDisplayId(e.target.value)}
          className={inputCls}
        />
      </Field>
      <Field label="ONU ID" hint="display_id minus (port * 100)">
        <input
          type="number"
          value={onuId}
          onChange={(e) => setOnuId(e.target.value)}
          className={inputCls}
        />
      </Field>
      <Field label="Serial">
        <input
          type="text"
          value={serial}
          onChange={(e) => setSerial(e.target.value)}
          className={inputCls}
          spellCheck={false}
        />
      </Field>
      <Field label="Profile (optional)" hint="bandwidth profile">
        <SelectOrCustom
          value={profile}
          onChange={setProfile}
          options={knownBandwidth}
          placeholder="Select a profile…"
          className={inputCls}
        />
      </Field>
      <Field label="VNET / VLAN (optional)">
        <SelectOrCustom
          value={vnet}
          onChange={setVnet}
          options={knownVnet}
          placeholder="Select a VNET / type a VLAN…"
          className={inputCls}
        />
      </Field>
      <div className="sm:col-span-2 flex items-center gap-2">
        <button
          type="button"
          onClick={() => previewMut.mutate()}
          disabled={previewMut.isPending || !port || !displayId || !serial}
          className="inline-flex items-center gap-1 rounded border border-slate-700 bg-slate-800 px-3 py-1.5 text-xs text-slate-200 hover:bg-slate-700 disabled:opacity-50"
        >
          <FileSearch className="h-3 w-3" />
          {previewMut.isPending ? "Rendering…" : "Preview CLI"}
        </button>
        <button
          type="button"
          onClick={run}
          disabled={!port || !displayId || !serial}
          className="inline-flex items-center gap-1 rounded border border-red-800 bg-red-900/30 px-3 py-1.5 text-xs text-red-200 hover:bg-red-900/50 disabled:opacity-50"
        >
          <Play className="h-3 w-3" />
          Submit provision (destructive)
        </button>
      </div>
      <p className="sm:col-span-2 -mt-1 text-[10px] text-slate-500">
        For fresh ONUs use the Pending ONUs flow on the chassis page
        — it pre-fills these fields and adds the service VNET blocks.
        This direct form provisions the base ONU only (recovery / edge
        cases).
      </p>
      {preview && (
        <div className="sm:col-span-2">
          <CommandPreview text={preview} />
        </div>
      )}
      {confirmDialog}
    </div>
  );
}

// ----------------------------------------------------------------------
// Deprovision ONU
// ----------------------------------------------------------------------

/** WS-H — batch endpoint wire shapes. The server chunks the selection
 *  into ``deprovision_onu_batch`` jobs of 5 targets (clamp 1..10). */
type DeprovisionBatchTarget = { port: string; display_id: string };
type DeprovisionBatchResponse = {
  jobs: { job_id: string; targets: DeprovisionBatchTarget[] }[];
  queued: number;
};

function DeprovisionOnuForm({
  chassisId,
  prefill,
}: {
  chassisId: string;
  prefill?: ManualOpPrefill;
}) {
  const [port, setPort] = useState(prefill?.port ?? "");
  const [displayId, setDisplayId] = useState(prefill?.displayId ?? "");
  // WS-H — batch mode: ticking 2+ ONUs on the port submits ONE call to
  // the deprovision-batch endpoint instead of a single-ONU job. A
  // single tick behaves exactly like typing that ID (single path).
  const [multi, setMulti] = useState<string[]>([]);
  const [preview, setPreview] = useState<string | null>(null);
  const { submit, confirmDialog } = useSubmitDestructive(chassisId);
  const { confirm: confirmBatch, confirmDialog: batchConfirmDialog } =
    useConfirm();
  const toast = useToast();
  const { ports, onusForPort } = useInventory(chassisId);

  const portOnus = onusForPort(port);
  const batchMode = multi.length >= 2;
  const singleId = multi.length === 1 ? multi[0] : displayId;

  const toggleMulti = (id: string) => {
    setMulti((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
    );
  };

  const previewMut = useMutation({
    mutationFn: async () => {
      const res = await api.post<CliPreview>(
        `/chassis/${chassisId}/maintenance/deprovision-onu/preview`,
        { port, display_id: singleId },
      );
      return res.data;
    },
    onSuccess: (d) => setPreview(d.rendered_block),
    onError: () => toast.push("Preview failed — check the inputs", "error"),
  });

  const runSingle = async () => {
    const summary = `deprovision ${port}/${singleId}`;
    const queued = await submit(
      "deprovision_onu",
      { port, display_id: singleId },
      summary,
      preview,
    );
    if (queued) setPreview(null);
  };

  const runBatch = async () => {
    // Sort numerically so the confirm list + job order read naturally.
    const ids = [...multi].sort((a, b) => Number(a) - Number(b));
    const summary =
      `deprovision ${ids.length} ONUs on port ${port} ` +
      `(in batches of 5):\n` +
      ids.map((id) => `  ${port}/${id}`).join("\n");
    const ok = await confirmBatch({
      title: "Confirm deprovision_onu_batch",
      summary,
      confirmLabel: "Submit (destructive)",
    });
    if (!ok) return;
    try {
      const res = await api.post<DeprovisionBatchResponse>(
        `/chassis/${chassisId}/maintenance/deprovision-batch`,
        { targets: ids.map((id) => ({ port, display_id: id })) },
      );
      for (const job of res.data.jobs) {
        window.dispatchEvent(
          new CustomEvent("inline-job-tracker.add", {
            detail: { jobId: job.job_id, kind: "deprovision_onu_batch" },
          }),
        );
      }
      toast.push(
        `${res.data.queued} deprovisions queued across ${res.data.jobs.length} job${res.data.jobs.length === 1 ? "" : "s"}`,
        "success",
      );
      setMulti([]);
      setPreview(null);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error("deprovision batch submit failed", err);
      toast.push(apiErrorMessage(err, "Submit failed — check the form"), "error");
    }
  };

  return (
    <div className="grid gap-3 sm:grid-cols-2">
      <Field label="Port" hint="from inventory — or type a custom value">
        <SelectOrCustom
          value={port}
          onChange={(v) => {
            setPort(v);
            setDisplayId(""); // display IDs depend on the chosen port
            setMulti([]);
          }}
          options={ports}
          placeholder="Select a PON port…"
          className={inputCls}
        />
      </Field>
      <Field
        label="Display ID"
        hint={
          batchMode
            ? "ignored — batch selection below is in charge"
            : port
              ? "ONUs on this port"
              : "pick a port first"
        }
      >
        <SelectOrCustom
          value={displayId}
          onChange={(v) => {
            setDisplayId(v);
            setMulti([]); // typing an ID reverts to the single path
          }}
          options={portOnus.map((o) => o.display_id)}
          placeholder="Select a display ID…"
          className={inputCls}
        />
      </Field>
      {port && portOnus.length > 0 && (
        <div className="sm:col-span-2 flex flex-col gap-1">
          <span className="text-xs text-slate-400">
            Batch select{" "}
            <span className="text-[10px] text-slate-500">
              — tick 2+ ONUs to deprovision them in batches of 5 (one
              failed/absent target won&apos;t halt the rest)
            </span>
          </span>
          <div className="max-h-48 overflow-y-auto rounded border border-slate-700 bg-slate-950 p-2">
            <div className="grid gap-1 sm:grid-cols-2">
              {portOnus.map((o) => (
                <label
                  key={o.display_id}
                  className="flex cursor-pointer items-center gap-2 rounded px-1 py-0.5 text-xs text-slate-200 hover:bg-slate-800"
                >
                  <input
                    type="checkbox"
                    checked={multi.includes(o.display_id)}
                    onChange={() => toggleMulti(o.display_id)}
                    className="accent-red-600"
                  />
                  <span className="font-mono">{o.display_id}</span>
                  {o.serial && (
                    <span className="truncate text-[10px] text-slate-500">
                      {o.serial}
                    </span>
                  )}
                </label>
              ))}
            </div>
          </div>
          {batchMode && (
            <span className="text-[10px] text-amber-400">
              {multi.length} ONUs selected — submits {multi.length} targets
              in batches of 5
            </span>
          )}
        </div>
      )}
      <div className="sm:col-span-2 flex items-center gap-2">
        <button
          type="button"
          onClick={() => previewMut.mutate()}
          disabled={previewMut.isPending || !port || !singleId || batchMode}
          className="inline-flex items-center gap-1 rounded border border-slate-700 bg-slate-800 px-3 py-1.5 text-xs text-slate-200 hover:bg-slate-700 disabled:opacity-50"
        >
          <FileSearch className="h-3 w-3" />
          {previewMut.isPending ? "Rendering…" : "Preview CLI"}
        </button>
        <button
          type="button"
          onClick={batchMode ? runBatch : runSingle}
          disabled={!port || (batchMode ? false : !singleId)}
          className="inline-flex items-center gap-1 rounded border border-red-800 bg-red-900/30 px-3 py-1.5 text-xs text-red-200 hover:bg-red-900/50 disabled:opacity-50"
        >
          <Play className="h-3 w-3" />
          {batchMode
            ? `Submit batch deprovision (${multi.length} ONUs, destructive)`
            : "Submit deprovision (destructive)"}
        </button>
      </div>
      {preview && !batchMode && (
        <div className="sm:col-span-2">
          <CommandPreview text={preview} />
        </div>
      )}
      {confirmDialog}
      {batchConfirmDialog}
    </div>
  );
}

// ----------------------------------------------------------------------
// Swap ONU
// ----------------------------------------------------------------------

function SwapOnuForm({
  chassisId,
  prefill,
}: {
  chassisId: string;
  prefill?: ManualOpPrefill;
}) {
  const [port, setPort] = useState(prefill?.port ?? "");
  const [displayId, setDisplayId] = useState(prefill?.displayId ?? "");
  const [oldSerial, setOldSerial] = useState(prefill?.serial ?? "");
  const [newSerial, setNewSerial] = useState("");
  const [preview, setPreview] = useState<string | null>(null);
  const { submit, confirmDialog } = useSubmitDestructive(chassisId);
  const toast = useToast();

  const previewMut = useMutation({
    mutationFn: async () => {
      const res = await api.post<CliPreview>(
        `/chassis/${chassisId}/maintenance/swap-onu/preview`,
        {
          port,
          display_id: displayId,
          old_serial: oldSerial,
          new_serial: newSerial,
        },
      );
      return res.data;
    },
    onSuccess: (d) => setPreview(d.rendered_block),
    onError: () => toast.push("Preview failed — check the inputs", "error"),
  });

  const run = async () => {
    const summary = `swap ${port}/${displayId} ${oldSerial} -> ${newSerial}`;
    const queued = await submit(
      "swap_onu",
      {
        port,
        display_id: displayId,
        old_serial: oldSerial,
        new_serial: newSerial,
      },
      summary,
      preview,
    );
    if (queued) setPreview(null);
  };

  return (
    <div className="grid gap-3 sm:grid-cols-2">
      <Field label="Port">
        <input
          type="text"
          value={port}
          onChange={(e) => setPort(e.target.value)}
          className={inputCls}
        />
      </Field>
      <Field label="Display ID">
        <input
          type="text"
          value={displayId}
          onChange={(e) => setDisplayId(e.target.value)}
          className={inputCls}
        />
      </Field>
      <Field label="Old serial">
        <input
          type="text"
          value={oldSerial}
          onChange={(e) => setOldSerial(e.target.value)}
          className={inputCls}
          spellCheck={false}
        />
      </Field>
      <Field label="New serial">
        <input
          type="text"
          value={newSerial}
          onChange={(e) => setNewSerial(e.target.value)}
          className={inputCls}
          spellCheck={false}
        />
      </Field>
      <div className="sm:col-span-2 flex items-center gap-2">
        <button
          type="button"
          onClick={() => previewMut.mutate()}
          disabled={
            previewMut.isPending ||
            !port ||
            !displayId ||
            !oldSerial ||
            !newSerial
          }
          className="inline-flex items-center gap-1 rounded border border-slate-700 bg-slate-800 px-3 py-1.5 text-xs text-slate-200 hover:bg-slate-700 disabled:opacity-50"
        >
          <FileSearch className="h-3 w-3" />
          {previewMut.isPending ? "Rendering…" : "Preview CLI"}
        </button>
        <button
          type="button"
          onClick={run}
          disabled={!port || !displayId || !oldSerial || !newSerial}
          className="inline-flex items-center gap-1 rounded border border-red-800 bg-red-900/30 px-3 py-1.5 text-xs text-red-200 hover:bg-red-900/50 disabled:opacity-50"
        >
          <Play className="h-3 w-3" />
          Submit swap (destructive)
        </button>
      </div>
      {preview && (
        <div className="sm:col-span-2">
          <CommandPreview text={preview} />
        </div>
      )}
      {confirmDialog}
    </div>
  );
}

// ----------------------------------------------------------------------
// Config profiles
// ----------------------------------------------------------------------

type Profile = {
  id: number;
  category: string;
  name: string;
  payload: Record<string, string>;
  usage_count: number;
  raw_config: string;
  seen_at: string;
};
type ProfileListResponse = {
  profiles: Profile[];
  latest_backup_id: number | null;
  parsed_profiles_at: string | null;
};

const PROFILE_TONE: Record<string, BadgeTone> = {
  shaper: "info",
  bandwidth: "info",
  vnet: "success",
  cos: "warning",
  cosq: "warning",
};

export function ProfilesPanel({ chassisId }: { chassisId: string }) {
  const qc = useQueryClient();
  const toast = useToast();

  const q = useQuery<ProfileListResponse>({
    queryKey: ["v2", "chassis", chassisId, "profiles"],
    queryFn: async () => {
      const res = await api.get<ProfileListResponse>(
        `/chassis/${chassisId}/profiles`,
      );
      return res.data;
    },
    refetchInterval: 60_000,
  });

  const reparse = useMutation({
    mutationFn: async () => {
      const res = await api.post<{ id: string }>(`/chassis/${chassisId}/jobs`, {
        name: "parse_profiles",
        params: {},
      });
      return res.data;
    },
    onSuccess: ({ id }) => {
      window.dispatchEvent(
        new CustomEvent("inline-job-tracker.add", {
          detail: { jobId: id, kind: "parse_profiles" },
        }),
      );
      toast.push("parse_profiles queued", "success");
      setTimeout(() => {
        qc.invalidateQueries({
          queryKey: ["v2", "chassis", chassisId, "profiles"],
        });
      }, 5_000);
    },
    onError: () => toast.push("Re-parse failed to queue", "error"),
  });

  const profiles = q.data?.profiles ?? [];

  // Group by category for display.
  const byCategory = useMemo(() => {
    const m = new Map<string, Profile[]>();
    for (const p of profiles) {
      const cat = p.category || "other";
      (m.get(cat) ?? m.set(cat, []).get(cat))!.push(p);
    }
    return Array.from(m.entries())
      .map(([cat, list]) => ({ cat, list: list.sort((a, b) => a.name.localeCompare(b.name)) }))
      .sort((a, b) => a.cat.localeCompare(b.cat));
  }, [profiles]);

  return (
    <section className="rounded-lg bg-slate-900 shadow-border p-4">
      <div className="mb-2 flex items-baseline justify-between">
        <div>
          <h3 className="text-sm font-medium text-slate-200">
            Config profiles (from latest backup)
          </h3>
          <p className="text-[10px] text-slate-500">
            {q.data?.latest_backup_id != null
              ? `parsed from backup #${q.data.latest_backup_id}`
              : "no backup available — take one from the Backups tab first"}
            {q.data?.parsed_profiles_at &&
              ` · last parsed ${formatLocal(q.data.parsed_profiles_at)}`}
          </p>
        </div>
        <button
          type="button"
          onClick={() => reparse.mutate()}
          disabled={reparse.isPending}
          className="inline-flex items-center gap-1 rounded border border-slate-700 bg-slate-800 px-2 py-1 text-xs text-slate-200 hover:bg-slate-700 disabled:opacity-50"
        >
          <RefreshCw className="h-3 w-3" />
          {reparse.isPending ? "Queuing…" : "Re-parse"}
        </button>
      </div>
      {q.isLoading ? (
        <p className="text-xs text-slate-500">Loading…</p>
      ) : byCategory.length === 0 ? (
        <p className="text-xs text-slate-500">
          No profiles parsed yet. Take a backup, then click Re-parse.
        </p>
      ) : (
        <div className="flex flex-col gap-3">
          {byCategory.map(({ cat, list }) => (
            <details key={cat} open className="rounded border border-slate-800">
              <summary className="cursor-pointer px-2 py-1 text-xs uppercase tracking-wide text-slate-400">
                <Badge tone={PROFILE_TONE[cat] ?? "neutral"}>{cat}</Badge>
                <span className="ml-2 text-slate-500">
                  {list.length} profile{list.length === 1 ? "" : "s"}
                </span>
              </summary>
              <table className="w-full text-xs">
                <thead className="text-left text-[10px] uppercase tracking-wide text-slate-500">
                  <tr>
                    <th className="px-2 py-1">Name</th>
                    <th className="px-2 py-1">Usage</th>
                    <th className="px-2 py-1">Payload</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-800/40 text-slate-300">
                  {list.map((p) => (
                    <tr key={p.id}>
                      <td className="px-2 py-1.5">
                        <CopyableCell value={p.name} />
                      </td>
                      <td className="px-2 py-1.5 font-mono">
                        {p.usage_count.toLocaleString()}
                      </td>
                      <td className="px-2 py-1.5 text-[10px] text-slate-400">
                        {Object.entries(p.payload ?? {})
                          .slice(0, 4)
                          .map(([k, v]) => `${k}=${v}`)
                          .join(" · ") || "—"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </details>
          ))}
        </div>
      )}
    </section>
  );
}

// ----------------------------------------------------------------------
// Verify config
// ----------------------------------------------------------------------

type VerifyReport = {
  total_onus: number;
  valid_count: number;
  issue_count: number;
  parsed_at: string | null;
  latest_backup_id: number | null;
  issues: {
    port: string;
    display_id: string;
    serial: string | null;
    code: string;
    detail: string;
  }[];
};

export function VerifyPanel({ chassisId }: { chassisId: string }) {
  const qc = useQueryClient();
  const toast = useToast();

  const q = useQuery<VerifyReport>({
    queryKey: ["v2", "chassis", chassisId, "verification"],
    queryFn: async () => {
      const res = await api.get<VerifyReport>(
        `/chassis/${chassisId}/verification`,
      );
      return res.data;
    },
    refetchInterval: 60_000,
  });

  const reverify = useMutation({
    mutationFn: async () => {
      const res = await api.post<{ id: string }>(`/chassis/${chassisId}/jobs`, {
        name: "verify_config",
        params: {},
      });
      return res.data;
    },
    onSuccess: ({ id }) => {
      window.dispatchEvent(
        new CustomEvent("inline-job-tracker.add", {
          detail: { jobId: id, kind: "verify_config" },
        }),
      );
      toast.push("verify_config queued", "success");
      setTimeout(() => {
        qc.invalidateQueries({
          queryKey: ["v2", "chassis", chassisId, "verification"],
        });
      }, 5_000);
    },
    onError: () => toast.push("Verify failed to queue", "error"),
  });

  return (
    <section className="rounded-lg bg-slate-900 shadow-border p-4">
      <div className="mb-2 flex items-baseline justify-between">
        <div>
          <h3 className="text-sm font-medium text-slate-200">
            Verify config (from latest backup)
          </h3>
          <p className="text-[10px] text-slate-500">
            {q.data?.latest_backup_id != null
              ? `against backup #${q.data.latest_backup_id}`
              : "no backup available — take one first"}
            {q.data?.parsed_at &&
              ` · last verified ${formatLocal(q.data.parsed_at)}`}
          </p>
        </div>
        <button
          type="button"
          onClick={() => reverify.mutate()}
          disabled={reverify.isPending}
          className="inline-flex items-center gap-1 rounded border border-slate-700 bg-slate-800 px-2 py-1 text-xs text-slate-200 hover:bg-slate-700 disabled:opacity-50"
        >
          <RefreshCw className="h-3 w-3" />
          {reverify.isPending ? "Queuing…" : "Re-verify"}
        </button>
      </div>
      {q.isLoading ? (
        <p className="text-xs text-slate-500">Loading…</p>
      ) : !q.data || q.data.total_onus === 0 ? (
        <p className="text-xs text-slate-500">
          No verify run yet. Take a backup, then click Re-verify.
        </p>
      ) : (
        <>
          <div className="mb-2 flex items-center gap-2 text-xs">
            <Badge tone="success">{q.data.valid_count} valid</Badge>
            <Badge tone={q.data.issue_count > 0 ? "danger" : "muted"}>
              {q.data.issue_count} issues
            </Badge>
            <span className="text-slate-500">
              of {q.data.total_onus} ONUs
            </span>
          </div>
          {q.data.issue_count > 0 && (
            <table className="w-full text-xs">
              <thead className="text-left text-[10px] uppercase tracking-wide text-slate-500">
                <tr>
                  <th className="px-2 py-1">Port/ID</th>
                  <th className="px-2 py-1">Serial</th>
                  <th className="px-2 py-1">Issue</th>
                  <th className="px-2 py-1">Detail</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-800/40 text-slate-300">
                {q.data.issues.map((issue, i) => (
                  <tr key={i}>
                    <td className="px-2 py-1.5 font-mono">
                      <CopyableCell value={`${issue.port}/${issue.display_id}`} />
                    </td>
                    <td className="px-2 py-1.5">
                      <CopyableCell value={issue.serial} />
                    </td>
                    <td className="px-2 py-1.5">
                      <Badge tone="warning">{issue.code}</Badge>
                    </td>
                    <td className="px-2 py-1.5 text-[11px] text-slate-400">
                      <ChevronRight className="inline h-3 w-3" />
                      {issue.detail}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </>
      )}
    </section>
  );
}
